/**
 * pdfFrame.js — client copy of the PDF page-frame reprojection math.
 *
 * A "frame" describes the coordinate system of one PDF page:
 *   { w, h, rotation, ox, oy }
 *   w / h      — UNROTATED page box width/height in PDF points (pdf.js `page.view`)
 *   rotation   — the page /Rotate value, normalized to 0 | 90 | 180 | 270
 *   ox / oy    — page box origin (x0, y0)
 *
 * QC annotations are stored in absolute PDF user-space coordinates of the PDF they
 * were placed on, PLUS the frame they were placed in (`page_frame` on findings and
 * qc_versions). When the currently displayed PDF uses a different frame, points are
 * reprojected through visual space (rotation-aware page fractions) so the markup
 * stays on the drawing. See docs/QC_WORKFLOW.md ("Page frames & re-anchoring").
 *
 * KEEP IN SYNC: api/_pdfFrame.js is the server-side original. The pure math here
 * (normalizeRotation, isFrame, visualSize, toVisualFraction, fromVisualFraction,
 * reprojectPoint, framesEqual, visualAspect, aspectsMatch, sizeScale,
 * reprojectShape, reprojectAnchor) must stay byte-for-byte identical to that file
 * — api/ and frontend/ can't share imports, so this is a maintained duplicate.
 */

/** Normalize a /Rotate value to 0 | 90 | 180 | 270. */
export function normalizeRotation(r) {
  let n = Math.round((Number(r) || 0) / 90) * 90
  n = ((n % 360) + 360) % 360
  return n
}

/** True when the value looks like a usable frame object. */
export function isFrame(f) {
  return !!(f && typeof f === 'object' && Number.isFinite(f.w) && Number.isFinite(f.h) && f.w > 0 && f.h > 0)
}

/** Displayed (post-rotation) page size in points: { vw, vh }. */
export function visualSize(frame) {
  const rot = normalizeRotation(frame.rotation)
  return (rot === 90 || rot === 270) ? { vw: frame.h, vh: frame.w } : { vw: frame.w, vh: frame.h }
}

/** User-space point -> visual page fraction [fx, fy] (0..1, top-left origin, y down). */
export function toVisualFraction(pt, frame) {
  const ox = frame.ox || 0, oy = frame.oy || 0
  const nx = (pt[0] - ox) / frame.w
  const ny = (pt[1] - oy) / frame.h
  const rot = normalizeRotation(frame.rotation)
  if (rot === 90)  return [ny, nx]
  if (rot === 180) return [1 - nx, ny]
  if (rot === 270) return [1 - ny, 1 - nx]
  return [nx, 1 - ny]
}

/** Visual page fraction [fx, fy] -> user-space point in the given frame. */
export function fromVisualFraction(frac, frame) {
  const ox = frame.ox || 0, oy = frame.oy || 0
  const rot = normalizeRotation(frame.rotation)
  let nx, ny
  if (rot === 90)       { nx = frac[1];     ny = frac[0] }
  else if (rot === 180) { nx = 1 - frac[0]; ny = frac[1] }
  else if (rot === 270) { nx = 1 - frac[1]; ny = 1 - frac[0] }
  else                  { nx = frac[0];     ny = 1 - frac[1] }
  return [ox + nx * frame.w, oy + ny * frame.h]
}

/** Map a user-space point from oldFrame's coordinate system into newFrame's. */
export function reprojectPoint(pt, oldFrame, newFrame) {
  return fromVisualFraction(toVisualFraction(pt, oldFrame), newFrame)
}

/** Frames equal within eps points (rotation exact). */
export function framesEqual(a, b, eps = 0.5) {
  if (!isFrame(a) || !isFrame(b)) return false
  if (normalizeRotation(a.rotation) !== normalizeRotation(b.rotation)) return false
  return Math.abs(a.w - b.w) <= eps && Math.abs(a.h - b.h) <= eps &&
    Math.abs((a.ox || 0) - (b.ox || 0)) <= eps && Math.abs((a.oy || 0) - (b.oy || 0)) <= eps
}

/** Visual aspect ratio (displayed width / displayed height). */
export function visualAspect(frame) {
  const { vw, vh } = visualSize(frame)
  return vw / vh
}

/** True when both frames show the same visual shape (aspect within tol, default 2%). */
export function aspectsMatch(a, b, tol = 0.02) {
  if (!isFrame(a) || !isFrame(b)) return false
  const ra = visualAspect(a), rb = visualAspect(b)
  return Math.abs(ra - rb) / rb <= tol
}

/** Scalar for stroke widths / font sizes / box widths when moving between frames. */
export function sizeScale(oldFrame, newFrame) {
  return visualSize(newFrame).vw / visualSize(oldFrame).vw
}

const round2 = v => Math.round(v * 100) / 100

/**
 * Reproject one annotation shape from oldFrame to newFrame (points, text bend-arrow,
 * and the frame-relative sizes: strokeWidth, fontSize, box width). Pure — returns a
 * new shape object.
 */
export function reprojectShape(s, oldFrame, newFrame) {
  const k = sizeScale(oldFrame, newFrame)
  const mapPt = p => { const q = reprojectPoint(p, oldFrame, newFrame); return [round2(q[0]), round2(q[1])] }
  const out = { ...s, points: (s.points || []).map(mapPt) }
  if (Number.isFinite(s.strokeWidth)) out.strokeWidth = round2(s.strokeWidth * k)
  if (Number.isFinite(s.fontSize)) out.fontSize = round2(s.fontSize * k)
  if (Number.isFinite(s.w)) out.w = round2(s.w * k)
  if (s.arrow && Array.isArray(s.arrow.bend) && Array.isArray(s.arrow.tip)) {
    out.arrow = { ...s.arrow, bend: mapPt(s.arrow.bend), tip: mapPt(s.arrow.tip) }
  }
  return out
}

/**
 * Reproject an { page, x, y, ... } anchor from oldFrame to newFrame. Pure — returns
 * a new anchor object (extra keys like `approx` are preserved).
 */
export function reprojectAnchor(anchor, oldFrame, newFrame) {
  if (!anchor || !Number.isFinite(anchor.x) || !Number.isFinite(anchor.y)) return anchor
  const [x, y] = reprojectPoint([anchor.x, anchor.y], oldFrame, newFrame)
  return { ...anchor, x: round2(x), y: round2(y) }
}

/**
 * Frame from the page meta the markup tool keeps in pageMetaRef / annotation_json
 * pages ({ pageWidth, pageHeight, rotation, mediaBoxOrigin }). Null when absent.
 */
export function frameFromPageMeta(meta) {
  if (!meta || !Number.isFinite(meta.pageWidth) || !Number.isFinite(meta.pageHeight) || meta.pageWidth <= 0 || meta.pageHeight <= 0) return null
  const o = Array.isArray(meta.mediaBoxOrigin) ? meta.mediaBoxOrigin : [0, 0]
  return { w: meta.pageWidth, h: meta.pageHeight, rotation: normalizeRotation(meta.rotation || 0), ox: o[0] || 0, oy: o[1] || 0 }
}
